import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Alert, Card } from "react-bootstrap";
import { toast } from "react-toastify";
import { useAuth } from "../../hooks/useAuth";
import { useMutation } from "react-query";
import {
  courseCategoryOption,
  fetchActionsUtil,
  sendFormData,
} from "../../utils/helpers";
import Select from "react-select";
import { useFormik } from "formik";
import * as Yup from "yup";
import UploadImage from "../utils/UploadImage";
import CurrencyCustomInput from "../utils/CurrencyCustomInput";
import { useEffectOnce } from "../../utils/hooks";

const difficultyLevelOptions = [
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
  { value: "expert", label: "Expert" },
];

const currencyOptions = [
  { value: "USD", label: "USD" },
  { value: "NGN", label: "NGN" },
  { value: "GBP", label: "GBP" },
  { value: "EUR", label: "EUR" },
];

const validationSchema = Yup.object().shape({
  title: Yup.string().required("Course title is required"),
  description: Yup.string(),
  certification_category: Yup.string().required(
    "Certification type is required",
  ),
  price: Yup.number()
    .required("Price is required")
    .positive("Price must be greater than 0"),
  currency: Yup.string().required("Currency is required"),
  duration_hours: Yup.number().min(0, "Duration must be positive"),
  difficulty_level: Yup.string(),
  thumbnail_url: Yup.string(),
  is_active: Yup.number(),
  sort_order: Yup.number(),
});

function CourseCreationModal({
  show,
  onHide,
  onSuccess,
  mode = "create",
  course = null,
  refetch,
  certificationTypes = [],
}) {
  const { backendUrl, token } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);

  const initialValues = {
    title: course?.title || "",
    description: course?.description || "",
    certification_category: course?.certification_category || "",
    price: course?.price || "",
    currency: course?.currency || "USD",
    duration_hours: course?.duration_hours || "",
    difficulty_level: course?.difficulty_level || "beginner",
    thumbnail_url: course?.thumbnail_url ?? "",
    is_active: Number(course?.is_active) === 1 ? 1 : 0,
    sort_order: course?.sort_order || "",
  };

  useEffectOnce(() => {
    if (course) {
      setThumbnailPreview(course?.thumbnail_url || "");
    }
  });
  // Create mutation for course submission
  const createCourseMutation = useMutation(
    (payload) =>
      sendFormData(
        `${backendUrl}/api/partnership/courses`,

        payload,
      ),
    {
      onSuccess: ({ data }) => {
        toast.success(data?.message || "Course created successfully");
        if (refetch) refetch();
        onSuccess?.();
      },
      onError: (error) => {
        toast.error(error?.message || "Failed to create course");
      },
    },
  );

  const updateCourseMutation = useMutation(
    (payload) =>
      sendFormData(
        `${backendUrl}/api/partnership/update-course`,

        payload,
      ),
    {
      onSuccess: ({ data }) => {
        toast.success(data?.message || "Course updated successfully");
        if (refetch) refetch();
        onSuccess?.();
      },
      onError: (error) => {
        toast.error(error?.message || "Failed to update course");
      },
    },
  );

  const handleThumbnailChange = (file, setFieldValue) => {
    if (file) {
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error(
          "Please select a valid image file (JPEG, JPG, PNG, GIF, WEBP)",
        );
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }

      setFieldValue("thumbnail_url", file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setThumbnailPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();

      // Append basic fields
      formData.append("title", values.title);
      formData.append("description", values.description);
      formData.append("certification_category", values.certification_category);
      formData.append("price", values.price);
      formData.append("currency", values.currency);
      formData.append("duration_hours", values.duration_hours || 0);
      formData.append("difficulty_level", values.difficulty_level);
      formData.append("is_active", values.is_active);
      if (course && course?.id) {
        formData.append("id", course?.id || "");
      }
      formData.append("sort_order", values.sort_order || 0);

      // Append thumbnail if it's a file object
      if (values.thumbnail_url && values.thumbnail_url instanceof File) {
        formData.append("thumbnail", values.thumbnail_url);
      }

      if (mode === "create") {
        createCourseMutation.mutate(formData);
      } else {
        updateCourseMutation.mutate(formData);
      }
    } catch (error) {
      toast.error("Error processing form");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleClose = () => {
    setThumbnailPreview(null);
    formik.resetForm();
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {mode === "create" ? "Create New Course" : "Edit Course"}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={formik.handleSubmit}>
        <Modal.Body className="p-4">
          {/* Basic Information Section */}
          <h5 className="mb-4">Basic Information</h5>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Course Title *</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  placeholder="Enter course title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={formik.touched.title && !!formik.errors.title}
                />
                {formik.touched.title && formik.errors.title && (
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.title}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Certification Category *</Form.Label>
                <Select
                  classNamePrefix="form-select"
                  placeholder="Select certification category"
                  name="certification_category"
                  options={courseCategoryOption}
                  value={courseCategoryOption.find(
                    (cert) =>
                      cert.value === formik.values.certification_category,
                  )}
                  onChange={(option) =>
                    formik.setFieldValue(
                      "certification_category",
                      option?.value || "",
                    )
                  }
                  isSearchable
                />
                {formik.touched.certification_category &&
                  formik.errors.certification_category && (
                    <div className="text-danger small mt-1">
                      {formik.errors.certification_category}
                    </div>
                  )}
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  placeholder="Enter course description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    formik.touched.description && !!formik.errors.description
                  }
                />
                {formik.touched.description && formik.errors.description && (
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.description}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>

          {/* Course Details Section */}
          <h5 className="mb-4 mt-4">Course Details</h5>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Price *</Form.Label>
                <CurrencyCustomInput
                  name="price"
                  value={formik.values.price}
                  onValueChange={(value) =>
                    formik.setFieldValue("price", value)
                  }
                  placeholder="0.00"
                  isInvalid={formik.touched.price && !!formik.errors.price}
                  currencySymbol={
                    currencyOptions?.find(
                      (c) => c.value === formik.values.currency,
                    )?.label || "USD"
                  }
                />
                {formik.touched.price && formik.errors.price && (
                  <div className="text-danger small mt-1">
                    {formik.errors.price}
                  </div>
                )}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Currency *</Form.Label>
                <Select
                  classNamePrefix="form-select"
                  placeholder="Select currency"
                  name="currency"
                  options={currencyOptions}
                  value={currencyOptions?.find(
                    (c) => c.value === formik.values.currency,
                  )}
                  onChange={(option) =>
                    formik.setFieldValue("currency", option?.value || "USD")
                  }
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Duration (Hours)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  name="duration_hours"
                  placeholder="0"
                  value={formik.values.duration_hours}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    formik.touched.duration_hours &&
                    !!formik.errors.duration_hours
                  }
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Difficulty Level</Form.Label>
                <Select
                  classNamePrefix="form-select"
                  placeholder="Select difficulty level"
                  name="difficulty_level"
                  options={difficultyLevelOptions}
                  value={difficultyLevelOptions?.find(
                    (d) => d.value === formik.values.difficulty_level,
                  )}
                  onChange={(option) =>
                    formik.setFieldValue(
                      "difficulty_level",
                      option?.value || "beginner",
                    )
                  }
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Active</Form.Label>
                <Form.Check
                  type="switch"
                  id="is_active"
                  name="is_active"
                  checked={formik.values.is_active === 1}
                  onChange={(e) =>
                    formik.setFieldValue("is_active", e.target.checked ? 1 : 0)
                  }
                  label="Course is active"
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Thumbnail Section */}
          <h5 className="mb-4 mt-4">Course Thumbnail</h5>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Thumbnail Image</Form.Label>
                <div className="d-flex gap-3 align-items-start">
                  <div style={{ width: "150px" }}>
                    <UploadImage
                      onFileUpload={(e) =>
                        handleThumbnailChange(
                          e.target.files?.[0],
                          formik.setFieldValue,
                        )
                      }
                      image={
                        formik.values.thumbnail_url instanceof File
                          ? formik.values.thumbnail_url
                          : null
                      }
                      imageUrl={formik.values.thumbnail_url}
                      name="thumbnail_url"
                      size="w-100"
                      noCirle
                    />
                  </div>
                  <div>
                    <p className="text-muted small mb-2">
                      Supported formats: JPEG, JPG, PNG, GIF, WEBP
                    </p>
                    <p className="text-muted small">Max size: 5MB</p>
                  </div>
                </div>
              </Form.Group>
            </Col>
          </Row>

          {Object.keys(formik.errors).length > 0 && (
            <Alert variant="danger" className="mt-3">
              Please fix the errors above before submitting.
            </Alert>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={
              isSubmitting ||
              createCourseMutation.isLoading ||
              updateCourseMutation.isLoading
            }
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={
              isSubmitting ||
              !formik.isValid ||
              createCourseMutation.isLoading ||
              updateCourseMutation.isLoading
            }
          >
            {isSubmitting ||
            createCourseMutation.isLoading ||
            updateCourseMutation.isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                {mode === "create" ? "Creating..." : "Updating..."}
              </>
            ) : mode === "create" ? (
              "Create Course"
            ) : (
              "Update Course"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default CourseCreationModal;
